/**
 * Catalogue AGORA : Métier › Module › Sous-module, tables de dimension et sections transverses.
 * Source : design du 21/10/2026 (écrans Accueil / Support). Les libellés sont dans le dictionnaire
 * (clé `catalogue.<id>`). Chaque nœud est une ressource de droits (Verified Permissions) :
 *   Metier::"service" › Module::"service.hotline" › SousModule::"service.hotline.tickets"
 *
 * Ajouter un module / sous-module : le déclarer ici (+ libellés dans les 6 langues), puis enregistrer
 * son écran dans web/src/modules/registre.ts. Statut « a-developper » = écran de squelette.
 */

export const METIERS = ['service', 'etude', 'commerce', 'mes', 'qhse', 'achats', 'finance', 'it'] as const;
export type MetierId = (typeof METIERS)[number];

/** Icônes disponibles dans le design (composant Icon), plus les ajouts AGORA des métiers Achats et Finance. */
export type Icone =
  | 'home' | 'calendar' | 'wrench' | 'ticket' | 'shield' | 'truck' | 'scan' | 'chart' | 'box' | 'book'
  | 'plus' | 'minus' | 'check' | 'more' | 'search' | 'bell' | 'globe' | 'building' | 'chevron' | 'user'
  | 'clock' | 'filter' | 'settings' | 'map' | 'list' | 'edit' | 'trash' | 'spark' | 'layers' | 'grid'
  | 'close' | 'send' | 'sidebar' | 'back' | 'grip' | 'sortIcon' | 'lock' | 'mail' | 'key' | 'arrow'
  | 'panier' | 'portefeuille';

export type Statut = 'disponible' | 'a-developper';

export interface SousModule {
  id: string;            // metier.module.sousModule
  icone: Icone;
  statut: Statut;
  /** Filiales où le sous-module est actif (absent = toutes). */
  filiales?: string[];
}

export interface Module {
  id: string;            // metier.module
  icone: Icone;
  statut: Statut;
  filiales?: string[];
  sousModules: SousModule[];
}

export interface Metier {
  id: MetierId;
  icone: Icone;
  modules: Module[];
}

function sm(id: string, icone: Icone, statut: Statut = 'a-developper'): SousModule {
  return { id, icone, statut };
}
function mod(id: string, icone: Icone, sousModules: SousModule[] = [], statut: Statut = 'a-developper'): Module {
  return { id, icone, statut, sousModules };
}

export const CATALOGUE: readonly Metier[] = [
  {
    id: 'service', icone: 'wrench', modules: [
      mod('service.gmao', 'wrench', [sm('service.gmao.contrats', 'book'), sm('service.gmao.sites', 'map'), sm('service.gmao.planning', 'calendar')]),
      mod('service.field', 'truck'),
      mod('service.hotline', 'ticket', [sm('service.hotline.tickets', 'ticket'), sm('service.hotline.kb', 'book'), sm('service.hotline.sla', 'clock')]),
      // POC téléphonie (23/10/2026) : journal des appels (3CX), transcription, résumé et rattachement aux objets
      mod('service.telephonie', 'bell', [sm('service.telephonie.appels', 'bell', 'disponible')], 'disponible'),
    ],
  },
  {
    id: 'etude', icone: 'spark', modules: [
      mod('etude.rd', 'spark'), mod('etude.projection', 'layers'), mod('etude.methode', 'list'),
    ],
  },
  {
    // Cadrage du MVP (remplacement de Salesforce) : docs/COMMERCE-CADRAGE.md §6.
    id: 'commerce', icone: 'chart', modules: [
      mod('commerce.prospection', 'map', [
        sm('commerce.prospection.pistes', 'spark', 'disponible'), sm('commerce.prospection.concurrents', 'shield', 'disponible'),
        sm('commerce.prospection.salons', 'calendar'),
      ], 'disponible'),
      mod('commerce.vente', 'chart', [
        sm('commerce.vente.affaires', 'layers', 'disponible'), sm('commerce.vente.pipeline', 'chart', 'disponible'), sm('commerce.vente.devis', 'list', 'disponible'),
        sm('commerce.vente.validations', 'check', 'disponible'), sm('commerce.vente.passations', 'send', 'disponible'),
      ], 'disponible'),
      mod('commerce.catalogue', 'box', [
        sm('commerce.catalogue.solutions', 'layers', 'disponible'), sm('commerce.catalogue.tarifs', 'list', 'disponible'),
        sm('commerce.catalogue.delegations', 'lock', 'disponible'),
      ], 'disponible'),
      mod('commerce.sav', 'box'),
    ],
  },
  {
    id: 'mes', icone: 'calendar', modules: [
      mod('mes.gestion', 'layers'), mod('mes.essais', 'list'),
    ],
  },
  {
    id: 'qhse', icone: 'shield', modules: [
      mod('qhse.board', 'chart'),
      mod('qhse.evenements', 'shield', [sm('qhse.evenements.nc', 'shield'), sm('qhse.evenements.sse', 'shield')]),
      mod('qhse.audits', 'list'), mod('qhse.actions', 'check'), mod('qhse.risques', 'grid'),
    ],
  },
  {
    // Maquette du 26/10/2026 : sourcing transverse, puis un flux d'achat par famille de dépense
    // (projets, pièces SAV, support et moyens généraux) – docs/FINANCE-ACHATS-CADRAGE.md §3.
    id: 'achats', icone: 'panier', modules: [
      mod('achats.board', 'chart'),
      mod('achats.fournisseurs', 'building', [
        sm('achats.fournisseurs.panel', 'check'), sm('achats.fournisseurs.consultations', 'send'),
        sm('achats.fournisseurs.contrats', 'book'), sm('achats.fournisseurs.evaluations', 'chart'),
      ]),
      mod('achats.projets', 'layers', [
        sm('achats.projets.besoins', 'layers'), sm('achats.projets.commandes', 'list'),
        sm('achats.projets.suivi', 'clock'), sm('achats.projets.receptions', 'truck'),
      ]),
      mod('achats.sav', 'wrench', [
        sm('achats.sav.demandes', 'ticket'), sm('achats.sav.commandes', 'list'),
        sm('achats.sav.reappro', 'box'), sm('achats.sav.retours', 'back'),
      ]),
      mod('achats.generaux', 'box', [
        sm('achats.generaux.demandes', 'edit'), sm('achats.generaux.catalogues', 'grid'),
        sm('achats.generaux.commandes', 'list'), sm('achats.generaux.contrats', 'book'),
      ]),
    ],
  },
  {
    // Maquette du 26/10/2026 : contrôle de gestion, cycles clients et fournisseurs, trésorerie, comptabilité ;
    // Sage X3 reste la comptabilité légale – docs/FINANCE-ACHATS-CADRAGE.md §4.
    id: 'finance', icone: 'portefeuille', modules: [
      mod('finance.board', 'chart'),
      mod('finance.controle', 'grid', [
        sm('finance.controle.budgets', 'calendar'), sm('finance.controle.affaires', 'layers'),
        sm('finance.controle.investissements', 'building'), sm('finance.controle.reporting', 'chart'),
      ]),
      mod('finance.clients', 'user', [
        sm('finance.clients.facturation', 'list'), sm('finance.clients.recouvrement', 'clock'),
        sm('finance.clients.credit', 'shield'),
      ]),
      mod('finance.fournisseurs', 'building', [
        sm('finance.fournisseurs.factures', 'scan'), sm('finance.fournisseurs.reglements', 'send'),
        sm('finance.fournisseurs.frais', 'user'),
      ]),
      mod('finance.tresorerie', 'portefeuille', [
        sm('finance.tresorerie.previsions', 'chart'), sm('finance.tresorerie.banques', 'building'),
        sm('finance.tresorerie.cautions', 'lock'),
      ]),
      mod('finance.comptabilite', 'book', [
        sm('finance.comptabilite.clotures', 'check'), sm('finance.comptabilite.intragroupe', 'globe'),
      ]),
    ],
  },
  {
    id: 'it', icone: 'settings', modules: [
      mod('it.demandes', 'ticket', [sm('it.demandes.incidents', 'ticket', 'disponible')], 'disponible'),
      mod('it.parc', 'box'), mod('it.changements', 'settings'),
    ],
  },
];

/**
 * Portée d'un référentiel (décision du 22/10/2026, référentiels « mixtes ») :
 *  - `filiale` : chaque filiale a ses propres lignes (colonne `filiale` + RLS) – comme toutes les données métier ;
 *  - `groupe`  : référentiel commun au groupe, lisible par toutes les filiales (pas de colonne filiale).
 */
export type Portee = 'filiale' | 'groupe';

/** Tables de dimension transverses (référentiels). `proprietaire` = métier qui écrit la donnée. */
export interface Dimension {
  id: string;
  icone: Icone;
  statut: Statut;
  proprietaire: MetierId | 'it';
  portee: Portee;
  source?: 'agora' | 'entra' | 'sage-x3';
}

export const DIMENSIONS: readonly Dimension[] = [
  { id: 'tiers', icone: 'building', statut: 'disponible', proprietaire: 'commerce', portee: 'filiale', source: 'agora' },
  { id: 'contacts', icone: 'user', statut: 'disponible', proprietaire: 'commerce', portee: 'filiale', source: 'agora' },
  // Sites CLIENTS (installations) ; les sites physiques de PRODEVAL sont la dimension « batiments » ci-dessous.
  { id: 'sites', icone: 'map', statut: 'a-developper', proprietaire: 'mes', portee: 'filiale', source: 'agora' },
  { id: 'equipements', icone: 'wrench', statut: 'a-developper', proprietaire: 'mes', portee: 'filiale', source: 'agora' },
  { id: 'articles', icone: 'box', statut: 'a-developper', proprietaire: 'commerce', portee: 'groupe', source: 'sage-x3' },
  { id: 'collaborateurs', icone: 'user', statut: 'a-developper', proprietaire: 'it', portee: 'groupe', source: 'entra' },
  { id: 'filiales', icone: 'globe', statut: 'disponible', proprietaire: 'it', portee: 'groupe', source: 'agora' },
  // Bâtiments : sites physiques de PRODEVAL (Issue #233, C9), pour les incidents, les actifs, les calendriers par pays.
  // Nom choisi pour ne pas les confondre avec les sites clients. Table dim.batiment ; administrée avec « administrer ».
  { id: 'batiments', icone: 'building', statut: 'disponible', proprietaire: 'it', portee: 'groupe', source: 'agora' },
];

/** Sections transverses de la barre latérale (hors métiers). */
/**
 * Champs sensibles (entités Cedar Agora::Champ) : droit demandé EN PLUS de celui du nœud « parent » (où le champ est
 * affiché), valeur masquée si refusé. Sans parent dans Cedar : seule une politique explicite sur le champ y donne accès
 * (les profils de développement, sans contrainte de ressource, l'accordent). Ni menu ni activation propres.
 */
export const CHAMPS: { id: string; parent: string }[] = [
  // Coûts standard et marge des tarifs (Commerce › Catalogue › Tarifs).
  { id: 'commerce.catalogue.couts', parent: 'commerce.catalogue.tarifs' },
  // Coûts et marge des devis (Commerce › Vente › Devis).
  { id: 'commerce.vente.marge', parent: 'commerce.vente.devis' },
  // Trousseau du site (mots de passe des automates et équipements, outils.trousseau) : « lire » pour voir et afficher,
  // « modifier » pour écrire ; affiché dans la fiche d'un site (référentiel des sites).
  { id: 'sites.trousseau', parent: 'sites' },
];

export const PILOTAGE = [{ id: 'pilotage.indicateurs', icone: 'chart' as Icone, statut: 'a-developper' as Statut }];

/**
 * Outils personnels du socle, hors métiers et hors filiale : ils portent les données propres à l'utilisateur (sa
 * messagerie Microsoft 365…) et s'ouvrent depuis le DOCK (pas la barre latérale), si l'utilisateur les a activés dans
 * ses Réglages. Droits et activation sont évalués pour « ALL » (toutes ses filiales : actif si le nœud l'est dans au
 * moins une) et l'écriture reste possible dans la vue consolidée.
 */
export const OUTILS = [
  { id: 'outils.messagerie', icone: 'mail' as Icone, statut: 'disponible' as Statut },
  // Trousseau (29/10/2026) : mots de passe personnels, de service et des objets (sites) – entrée « Trousseau » du menu
  // Profil du dock (docs/TROUSSEAU.md).
  { id: 'outils.trousseau', icone: 'key' as Icone, statut: 'disponible' as Statut },
  // Scribe (30/10/2026) : enregistreur de réunions, bouton du dock activé dans les Réglages (docs/SCRIBE.md) ;
  // écran, dock et option des Réglages livrés au lot 1c ; icône définitive : décision R10.
  { id: 'outils.scribe', icone: 'edit' as Icone, statut: 'disponible' as Statut },
];

/**
 * Nœuds ouverts ailleurs que par les menus (barre latérale, accueil du métier, modules du dock, recherche) : ils restent
 * des ressources de droits, avec leur route et leur écran. Journal des appels (28/10/2026) : bouton « Journal des
 * appels » du panneau Téléphone du dock (web/src/core/telephonie/softphone/Softphone.tsx).
 */
export const HORS_MENU: ReadonlySet<string> = new Set(['service.telephonie', 'service.telephonie.appels']);

/** Sections transverses (nœuds de type Module sans métier) : pilotage et outils personnels. */
export type SectionTransverse = 'pilotage' | 'outils';

// ------------------------------------------------------------------ utilitaires
export type TypeNoeud = 'Metier' | 'Module' | 'SousModule' | 'Dimension';

export interface Noeud {
  id: string;
  type: TypeNoeud;
  icone: Icone;
  statut: Statut;
  metier?: MetierId;
  /** Section transverse d'un module sans métier (pilotage, outils). */
  section?: SectionTransverse;
  parents: string[];     // ids des ancêtres, du plus proche au plus lointain
  route: string;         // chemin du front
}

/** Tous les nœuds du catalogue à plat (ressources de droits + routes). */
export function noeuds(): Noeud[] {
  const out: Noeud[] = [];
  for (const m of CATALOGUE) {
    out.push({ id: m.id, type: 'Metier', icone: m.icone, statut: 'disponible', metier: m.id, parents: [], route: `/${m.id}` });
    for (const mo of m.modules) {
      const [, mm] = mo.id.split('.');
      out.push({ id: mo.id, type: 'Module', icone: mo.icone, statut: mo.statut, metier: m.id, parents: [m.id], route: `/${m.id}/${mm}` });
      for (const s of mo.sousModules) {
        const [, , ss] = s.id.split('.');
        out.push({ id: s.id, type: 'SousModule', icone: s.icone, statut: s.statut, metier: m.id, parents: [mo.id, m.id], route: `/${m.id}/${mm}/${ss}` });
      }
    }
  }
  for (const d of DIMENSIONS) {
    out.push({ id: d.id, type: 'Dimension', icone: d.icone, statut: d.statut, parents: [], route: `/referentiels/${d.id}` });
  }
  for (const p of PILOTAGE) {
    out.push({ id: p.id, type: 'Module', icone: p.icone, statut: p.statut, section: 'pilotage', parents: [], route: '/pilotage/indicateurs' });
  }
  for (const o of OUTILS) {
    out.push({ id: o.id, type: 'Module', icone: o.icone, statut: o.statut, section: 'outils', parents: [], route: `/${o.id.replace('.', '/')}` });
  }
  return out;
}

export function noeud(id: string): Noeud | undefined {
  return noeuds().find((n) => n.id === id);
}

/**
 * Nœuds activables par filiale (écran Administration › Modules) : métiers, modules, sous-modules, pilotage et outils.
 * Les référentiels restent toujours actifs. Un nœud est actif s'il l'est lui-même ET tous ses ancêtres.
 */
export function noeudsActivables(): Noeud[] {
  return noeuds().filter((n) => n.type !== 'Dimension');
}

/** Retrouve le nœud correspondant à un chemin du front (/service/hotline/tickets…). */
export function noeudDeRoute(chemin: string): Noeud | undefined {
  const propre = chemin.replace(/\/+$/, '') || '/';
  return noeuds().find((n) => n.route === propre);
}
